/**
 * DEC-1146 (FEEDBACK-2): a small ring buffer of recent client errors, attached
 * to a bug report as diagnostic context.
 *
 * WHY IT EXISTS. Users describe what they saw ("the page went blank"), not what
 * threw. Without this, every report needs a round-trip to reproduce. With it,
 * the report arrives carrying the actual error text from the seconds before the
 * user hit Send.
 *
 * ── Deliberate limits ────────────────────────────────────────────────────
 * MESSAGE ONLY — NO STACK TRACES (security ruling, v1). A stack embeds file
 * paths, bundle structure and sometimes inlined values; the message alone is
 * most of the diagnostic value at a fraction of the exposure. Revisit only
 * with a security pass, not casually.
 *
 * Every message is scrubbed AT CAPTURE via the DEC-1145 pipeline. That is a
 * courtesy pass — it keeps secrets off the network hop — and is NOT the
 * control. The server scrubs again before insert, because a client can send
 * anything. Never drop the server-side pass on the strength of this one.
 *
 * Cap 10 entries / 15-minute age. An error storm must not turn this into an
 * unbounded leak of everything that went wrong all session; the last handful
 * before the user complained is what's actually relevant.
 *
 * ── Self-safety ──────────────────────────────────────────────────────────
 * This module runs INSIDE the failure paths it observes, so every entry point
 * is wrapped in try/catch and its own errors are dropped rather than raised.
 * A reporter that throws while recording a throw would turn one bug into an
 * infinite loop — and would do it precisely when the app is already broken.
 */

import { scrubClientText } from '../utils/scrubClientText';

const MAX_ENTRIES = 10;
const MAX_AGE_MS = 15 * 60 * 1000;

let buffer = [];
let installed = false;

/** Drop entries older than the age cap. Called on every read and write. */
function prune(now) {
  buffer = buffer.filter((e) => now - e.ts <= MAX_AGE_MS);
}

/**
 * Record one error. Never throws — see the self-safety note above.
 *
 * @param {string} message   raw error text (scrubbed here)
 * @param {string} errorType 'error' | 'unhandledrejection' | 'react'
 */
export function recordError(message, errorType) {
  try {
    if (!message) return;
    const now = Date.now();
    prune(now);
    buffer.push({
      message: scrubClientText(String(message)),
      errorType,
      // PATH ONLY — never href. A query string here would reintroduce exactly
      // what the scrub exists to remove, and would do it on every entry.
      route: typeof window !== 'undefined' ? window.location.pathname : null,
      ts: now,
    });
    if (buffer.length > MAX_ENTRIES) {
      buffer = buffer.slice(-MAX_ENTRIES);
    }
  } catch {
    // Swallow. A failure to RECORD an error must never become an error.
  }
}

/** The current buffer, newest last. Safe copy — callers can't mutate ours. */
export function getRecentErrors() {
  try {
    prune(Date.now());
    return buffer.map((e) => ({ ...e }));
  } catch {
    return [];
  }
}

export function clearErrors() {
  buffer = [];
}

/**
 * Install the global listeners. Called from index.js BEFORE first render, so
 * an error thrown during the initial mount is still captured.
 *
 * Idempotent: a double-install would record every error twice and quietly
 * halve the buffer's useful depth.
 */
export function installErrorListeners(target = typeof window !== 'undefined' ? window : null) {
  if (!target || installed) return;
  installed = true;

  try {
    target.addEventListener('error', (event) => {
      // Resource load failures (a 404'd <img>) fire 'error' too but carry no
      // message and aren't what this is for.
      const msg = event?.message || event?.error?.message;
      if (msg) recordError(msg, 'error');
    });

    target.addEventListener('unhandledrejection', (event) => {
      const reason = event?.reason;
      const msg = typeof reason === 'string' ? reason : reason?.message;
      if (msg) recordError(msg, 'unhandledrejection');
    });
  } catch {
    // A browser that won't let us listen is a degraded bug report, not a
    // broken app.
    installed = false;
  }
}

/** Test seam. */
export function _resetForTests() {
  buffer = [];
  installed = false;
}

export const LIMITS = { MAX_ENTRIES, MAX_AGE_MS };
